import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AutoComplete,
  Button,
  Checkbox,
  Form,
  Input,
  notification,
} from "antd";
import Logo from "../../assets/image/logo_light.png";
import "./styles.css";
import { API } from "../../config/API";
import Lockmodal from "../../component/lock/index";
import { useDispatch } from "react-redux";
import { login } from "../../redux/slices/userslice";
import api from "../../config/axiosInstance";
import { requestGeolocation } from "../../utils/geolocation";

const OTP_VALIDITY_SECONDS = 180; // 3 minutes
const RESEND_COOLDOWN_SECONDS = 60; // 1 minute cooldown before resend

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const LoginScreen = (props: any) => {
  const dispatch = useDispatch();
  const navigate: any = useNavigate();
  const [loading, setLoading] = useState(false);
  const [otp, SetOtp] = useState(false);
  const [lockmodal, setlockModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>();
  const [lockTitle, setLockTitle] = useState<string>("");
  const [loginEmail, setLoginEmail] = useState<string>("");
  const [loginPassword, setLoginPassword] = useState<string>("");

  // OTP timer states
  const [otpTimer, setOtpTimer] = useState(OTP_VALIDITY_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const timerResetKey = useRef(0);

  let userDesc = localStorage.getItem("userlist");
  let userlistData: any = [];

  if (userDesc !== null) {
    let user = JSON.parse(userDesc);
    userlistData = user;
  }

  let uniqueUserlistData = userlistData?.filter(
    (value: any, index: any, self: any) => {
      return (
        index === self.findIndex((obj: { id: any }) => obj.id === value.id)
      );
    }
  );

  // Request browser GPS the moment the login page loads so we can attach
  // X-Latitude/X-Longitude headers to login attempts (including failures)
  // for the server-side audit log. Falls back to IP-geo if user denies.
  useEffect(() => {
    requestGeolocation();
  }, []);

  // Start / restart OTP timers. Called on first OTP send and on resend.
  const startOtpTimers = () => {
    timerResetKey.current += 1;
    setOtpTimer(OTP_VALIDITY_SECONDS);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setOtpExpired(false);
  };

  useEffect(() => {
    if (!otp) return;

    const key = timerResetKey.current;

    const interval = setInterval(() => {
      if (timerResetKey.current !== key) {
        clearInterval(interval);
        return;
      }
      setOtpTimer((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [otp, timerResetKey.current]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      if (otp) await verifyOTP(values);
      else await validateUser(values);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      notification.error({ message: "Error", description: "Something went wrong." });
    }
  };

  const verifyOTP = async (values: any) => {
    try {
      const response = await api.post(API.AUTH_VERIFY_OTP, {
        email: loginEmail,
        otp: values.otp,
      });

      const userData = response.data?.user;
      if (userData) {
        let obj = { id: userData.id, email: userData.email };
        userlistData.push(obj);
        localStorage.setItem("userlist", JSON.stringify(userlistData));
        dispatch(login(userData));
        requestGeolocation();
        const redirectPath = localStorage.getItem("redirectPath") || "/Auth";
        localStorage.removeItem("redirectPath");
        navigate(redirectPath, { replace: true });
      }
    } catch (error: any) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 410 && data?.expired) {
        notification.error({
          message: "OTP Expired",
          description: "Your OTP has expired. Please request a new one.",
        });
      } else {
        notification.error({
          message: "Verification Failed",
          description: data?.message || "OTP verification failed.",
        });
      }
    }
  };

  const validateUser = async (values: any) => {
    try {
      await api.post(API.AUTH_LOGIN, {
        email: values.email,
        password: values.password,
      });

      notification.success({
        message: "OTP Sent",
        description: "OTP has been sent to your email. It is valid for 3 minutes.",
      });
      setLoginEmail(values.email);
      setLoginPassword(values.password);
      SetOtp(true);
      startOtpTimers();
    } catch (error: any) {
      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 403 && data?.blocked) {
        setLockTitle("Your Account Has Been Blocked");
        setlockModal(true);
        setErrorMessage(
          "Your account has been blocked by the administrator. Please contact the administrator for assistance."
        );
      } else if (status === 423 && data?.isLocked) {
        setLockTitle("Your Account Has Been Locked");
        setlockModal(true);
        setErrorMessage(data.message);
      } else {
        notification.error({
          message: "Login Failed",
          description: data?.message || "Invalid email or password.",
        });
      }
    }
  };

  const handleResendOtp = async () => {
    try {
      setResendLoading(true);
      await api.post(API.AUTH_RESEND_OTP, { email: loginEmail, password: loginPassword });
      notification.success({
        message: "OTP Resent",
        description: "A new OTP has been sent to your email. It is valid for 3 minutes.",
      });
      startOtpTimers();
    } catch (error: any) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 401) {
        notification.error({
          message: "Invalid Credentials",
          description: data?.message || "Invalid email or password.",
        });
      } else if (status === 429) {
        notification.warning({
          message: "Too Soon",
          description: data?.message || "Please wait before requesting a new OTP.",
        });
      } else if (status === 403 && data?.blocked) {
        setLockTitle("Your Account Has Been Blocked");
        setlockModal(true);
        setErrorMessage(
          "Your account has been blocked by the administrator. Please contact the administrator for assistance."
        );
      } else if (status === 423 && data?.isLocked) {
        setLockTitle("Your Account Has Been Locked");
        setlockModal(true);
        setErrorMessage(data.message);
      } else {
        notification.error({
          message: "Resend Failed",
          description: data?.message || "Failed to resend OTP. Please try again.",
        });
      }
    } finally {
      setResendLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="loginSection">
      <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: "0 20px" }}>
        <div className="loginSection-box">
          <div className="loginSection-logobox">
            <img src={Logo} className="loginSection-logo" />
          </div>
          <br />
          <br />
          <Form
            name="basic"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
          >
            <div>E-mail</div>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Please enter a valid email address!" },
              ]}
            >
              <AutoComplete
                style={{ width: "100%" }}
                size="large"
                placeholder="Enter email"
                options={uniqueUserlistData.map((item: any) => ({
                  value: item?.email,
                  label: item?.email,
                }))}
                filterOption={(input: any, option: any) =>
                  option?.value?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0
                }
              />
            </Form.Item>
            <div>Password</div>
            <Form.Item
              name="password"
              rules={[{ message: "Please input your password!", required: true }]}
            >
              <Input.Password style={{ width: "100%" }} size="large" />
            </Form.Item>

            {otp && (
              <>
                {/* OTP label row with expiry timer */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span>OTP</span>
                  {/* <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: otpExpired ? "#ff4d4f" : otpTimer <= 30 ? "#fa8c16" : "#389e0d",
                    }}
                  >
                    {otpExpired ? "OTP Expired" : `Expires in ${formatTime(otpTimer)}`}
                  </span> */}
                </div>
                <Form.Item
                  name="otp"
                  rules={[{ message: "Please input your OTP!", required: true }]}
                >
                  <Input.Password style={{ width: "100%" }} size="large" placeholder="Enter OTP" />
                </Form.Item>

                {/* Resend OTP row */}
                <div style={{ textAlign: "right", marginTop: -14, marginBottom: 8 }}>
                  {resendCooldown > 0 ? (
                    <span style={{ color: "#999", fontSize: 13 }}>
                      Resend OTP in{" "}
                      <span style={{ fontWeight: 600, color: "#595959" }}>
                        {formatTime(resendCooldown)}
                      </span>
                    </span>
                  ) : (
                    <Button
                      type="link"
                      loading={resendLoading}
                      onClick={handleResendOtp}
                      style={{ padding: 0, fontSize: 13, height: "auto" }}
                    >
                      Didn't receive the OTP? Resend
                    </Button>
                  )}
                </div>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap" }}>
              <Form.Item name={"remember"} style={{ marginBottom: 0 }}>
                <Checkbox onChange={() => console.log("Checkbox")}>
                  <span style={{ whiteSpace: "nowrap" }}>Remember Me</span>
                </Checkbox>
              </Form.Item>
            </div>
            <br />
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                block
                style={{ height: 50 }}
              >
                {otp ? "Verify OTP" : "Login"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
      {lockmodal && (
        <Lockmodal
          visible={lockmodal}
          close={() => setlockModal(false)}
          data={errorMessage}
          title={lockTitle}
        />
      )}
    </div>
  );
};

export default LoginScreen;
